import React, { Fragment, useEffect, useState } from 'react'
import {
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { WarehouseDDL } from '../../../components/Almacen'
import {
  useOrderOrigins,
  useProductOrigins,
  useTypeAsnIncome,
  useTypePosition,
  useTypeMovement,
  useWarehouses,
  useWorkOrderState,
  useZones,
  useAsnState
} from '../../../hooks'
import { WorkOrderStateDDL } from '../../../components/_WMS/PrecreatedFilters/WorkOrderStateDDL'
import { useCourier } from '../../../hooks/useCourier'
import { Courier } from '../../../interfaces/Courier'
import { Filter, FilterComponentProps, FilterOption } from './types'
import { DateTime } from 'luxon'
import { TypePositionDDL } from '../../TypePosition'
import { TypeMovementDDL } from '../../TypeMovement'
import { ProductOrigin } from '../../../interfaces/Producto/ProductOrigins'
import { AsnState } from '../../../interfaces/Asn/AsnState'
import { PositionAutocomplete } from '../../Position/screens'
import { ZoneDDL } from '../../Zone'
import { useTypesOrder } from '../../../hooks/useTypeOrder'
import { eStatus, eTypeAction } from '../../../enums/eExternalAudit'
import RotateLeftIcon from '@mui/icons-material/RotateLeft'
import {
  eInventoryCheckState,
  eTypeInventoryCheck
} from '../../../enums/eInventoryCheck'
import { GenericDLL } from '../../Almacen/UI/GenericDLL'
import { eStatusProcess } from '../../../enums/eStatusProcess'
import { useTypeProcess } from '../../../hooks/useTypeProcess'
import {
  eBatchControl,
  eBatchExpiredStatus,
  eIsGreenSale
} from '../../../enums/eBatch'
import { useFlows } from '../../../hooks/useFlows'
import { useTypesAlert } from '../../../hooks/useTypeAlert'

export const FilterComponent: React.FC<FilterComponentProps> = ({
  options,
  setSelectedFilters,
  selectedFilters,
  onSearchClick,
  includeAddButton = false,
  includeRefreshSearch = false,
  requiredFilterKeys,
  defaultSelectedFilterKey
}) => {
  const {
    couriersState: { couriers }
  } = useCourier()

  const {
    warehousesState: { warehouses }
  } = useWarehouses()

  const {
    getTypeAsnIncome,
    typeAsnIncomeState: { typeAsnIncome }
  } = useTypeAsnIncome()

  const {
    workOrderStatesState: { workOrderStates }
  } = useWorkOrderState()

  const {
    typePositionsState: { typePositions }
  } = useTypePosition()

  const {
    typeMovementsState: { typeMovements },
    getTypeMovements
  } = useTypeMovement()

  const {
    getProductOrigins,
    productOriginsState: { productOrigins }
  } = useProductOrigins()

  const {
    zonesState: { zones }
  } = useZones()

  const {
    typesOrderState: { typesOrder }
  } = useTypesOrder()

  const {
    getOrderOrigins,
    orderOriginsState: { orderOrigins }
  } = useOrderOrigins()

  const {
    getTypeProcess,
    typeProcessState: { typeProcess }
  } = useTypeProcess()

  const {
    getTypesAlert,
    typesAlertState: { typesAlert }
  } = useTypesAlert()

  const {
    getFlows,
    flowsState: { flows }
  } = useFlows()

  const {
    asnStatesState: { asnState: asnStates },
    getAsnState
  } = useAsnState()

  const [dateRangeError, setDateRangeError] = useState<string>('')
  const [selectedFilterKey, setSelectedFilterKey] = useState<string | null>(
    defaultSelectedFilterKey || null
  )

  const [inputValue, setInputValue] = useState<string>('') // Estado para manejar el valor de entrada
  const [availableOptions, setAvailableOptions] = useState<FilterOption[]>([])

  useEffect(() => {
    setAvailableOptions(
      options.filter(
        (el) =>
          // Si el filtro puede ser repetido, SIEMPRE debe estar disponible
          el.canBeRepeated ||
          // Si no puede ser repetido, solo filtrarlo si ya existe en selectedFilters
          !selectedFilters.map((filter) => filter.key).includes(el.key)
      )
    )
  }, [selectedFilters, options])

  // guardar rango fechas filtros
  const [dateRangeStart, setDateRangeStart] = useState<string>('')
  const [dateRangeEnd, setDateRangeEnd] = useState<string>('')

  const handleDateRangeStartChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newStartDate = event.target.value
    setDateRangeStart(newStartDate)
    validateDateRange(newStartDate, dateRangeEnd)
  }

  const handleDateRangeEndChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newEndDate = event.target.value
    setDateRangeEnd(newEndDate)
    validateDateRange(dateRangeStart, newEndDate)
  }

  // validar rango fechas
  const validateDateRange = (startDate: string, endDate: string) => {
    if (startDate && endDate) {
      const start = new Date(startDate)
      const end = new Date(endDate)

      if (start > end) {
        setDateRangeError(
          'La fecha inicial no puede ser mayor que la fecha final'
        )
      } else {
        setDateRangeError('')
      }
    } else {
      setDateRangeError('')
    }
  }

  useEffect(() => {
    if (orderOrigins?.length === 0) {
      getOrderOrigins()
    }

    if (productOrigins?.length === 0) {
      getProductOrigins()
    }

    if (typeProcess?.length === 0) {
      getTypeProcess()
    }
    if (flows?.length === 0) {
      getFlows()
    }
    if (typesAlert?.length === 0) {
      getTypesAlert()
    }
    if (typeMovements?.length === 0) {
      getTypeMovements()
    }

    if (asnStates?.length === 0) {
      getAsnState()
    }

    if (typeAsnIncome?.length === 0) {
      getTypeAsnIncome()
    }
  }, [])

  // Determinar la opción seleccionada en base a selectedFilterKey
  const selectedOption = options.find(
    (option) => option.key === selectedFilterKey
  )

  const handleFilterSelection = (
    event: SelectChangeEvent<string>
  ) => {
    setSelectedFilterKey(event.target.value)
    // Limpiar el inputValue cuando se cambia de filtro para evitar valores residuales
    setInputValue('')
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value.toString())
  }

  const handleInputChangeNumericNotNegative = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value

    // Validar que solo se ingresen números no negativos
    if (/^\d*$/.test(value) || value === '') {
      setInputValue(value === '' ? '' : parseInt(value, 10).toString())
    }
  }

  const handleSearchButton = () => {
    if (
      selectedOption?.type === 'textempty' ||
      (selectedFilterKey && selectedOption && !includeAddButton)
    ) {
      addFilter()
    } else {
      onSearchClick(selectedFilters)
    }
  }

  const getDisplayValue = (option: string) => {
    switch (option) {
      case 'warehouseId':
        return (
          warehouses?.find((el) => el.id === parseInt(inputValue))
            ?.warehouseName || ''
        )
      case 'workOrderStateId':
        return (
          workOrderStates?.find(
            (el) => el.workOrderStateId === parseInt(inputValue)
          )?.state || ''
        )

      case 'courierId':
        return (
          couriers?.find((el) => el.courierId === parseInt(inputValue))?.name ||
          ''
        )

      case 'courierServiceId': {
        if (!couriers) return ''

        let serviceName = ''
        let courierName = ''

        // 1. Buscamos el courier padre que contenga este servicio
        const parentCourier = couriers.find((courier) =>
          courier.courierServices.some(
            (service) => service.courierServiceId === parseInt(inputValue)
          )
        )

        // 2. Si lo encontramos, obtenemos los nombres
        if (parentCourier) {
          courierName = parentCourier.name
          const service = parentCourier.courierServices.find(
            (s) => s.courierServiceId === parseInt(inputValue)
          )
          serviceName = service ? service.name : ''
        }

        // 3. Retornamos el mismo formato que el dropdown
        if (courierName && serviceName) {
          return `${courierName} - ${serviceName}`
        }

        return serviceName || '' // Fallback por si algo falla
      }

      case 'typePositionId':
        return (
          typePositions?.find(
            (el) => el.typePositionId === parseInt(inputValue)
          )?.typePositionDescription || ''
        )
      case 'typeMovementId':
        return (
          typeMovements?.find(
            (el) => el.typeMovementId === parseInt(inputValue)
          )?.description || ''
        )
      case 'externalStatusId':
        return eStatus[parseInt(inputValue)] || ''
      case 'typeActionId':
        return eTypeAction[parseInt(inputValue)] || ''
      case 'productOriginId':
      case 'brandId':
        return (
          productOrigins?.find(
            (el) => el.productOriginId === parseInt(inputValue)
          )?.name || ''
        )

      case 'zoneId':
        return (
          zones?.find((zone) => zone.zoneId === parseInt(inputValue))
            ?.zoneName || ''
        )
      case 'typeOrderId':
        return (
          typesOrder?.find((el) => el.typeOrderId === parseInt(inputValue))
            ?.name || ''
        )
      case 'orderStateId': {
        const orderStates = [
          { label: 'Creado', value: 1 },
          { label: 'En Espera De Stock', value: 4 },
          { label: 'En Preparación', value: 5 },
          { label: 'Solicitud De Cancelación', value: 2 },
          { label: 'Cancelado', value: 3 },
          { label: 'Gestión Completada', value: 6 }
        ]
        return (
          orderStates.find((state) => state.value === parseInt(inputValue))
            ?.label || ''
        )
      }
      case 'inventoryCheckStateId':
        return eInventoryCheckState[parseInt(inputValue)] || ''
      case 'typeInventoryCheckId':
        return eTypeInventoryCheck[parseInt(inputValue)] || ''
      case 'orderOriginId':
        return (
          orderOrigins?.find((el) => el.orderOriginId === parseInt(inputValue))
            ?.origin || ''
        )
      case 'typeProcessId':
        return (
          typeProcess?.find((el) => el.typeProcessId === parseInt(inputValue))
            ?.description || ''
        )
      case 'flowId':
        return (
          flows?.find((el) => el.flowId === parseInt(inputValue))
            ?.description || ''
        )
      case 'typeAlertId':
        return (
          typesAlert?.find((el) => el.typeAlertId === parseInt(inputValue))
            ?.name || ''
        )
      case 'typeAsnIncomeId':
        return (
          typeAsnIncome?.find(
            (el) => el.typeAsnIncomeId === parseInt(inputValue)
          )?.name || ''
        )
      case 'StatusProcessId':
        return eStatusProcess[parseInt(inputValue)] || ''
      case 'batchExpiredStatus':
        return eBatchExpiredStatus[parseInt(inputValue)] || ''
      case 'batchControl':
        return eBatchControl[parseInt(inputValue)] || ''
      case 'active':
        return inputValue === 'true' ? 'Sí' : inputValue === 'false' ? 'No' : ''
      case 'isDraft':
        return inputValue === 'true' ? 'Sí' : inputValue === 'false' ? 'No' : ''
      case 'asnStateId':
      case 'detailAsnStateId':
        return (
          asnStates?.find(
            (el: AsnState) => el.asnStateId === parseInt(inputValue)
          )?.name || inputValue
        )
      case 'isGreenSale':
        return eIsGreenSale[parseInt(inputValue)] || ''
      case 'initialDateFilter':
      case 'finalDateFilter':
      case 'initialExpirationDateFilter':
      case 'finalExpirationDateFilter':
        return inputValue // Las fechas ya vienen en formato correcto
      default:
        return undefined
    }
  }

  const isAddFilterButtonDisabled = (): boolean => {
    if (!selectedFilterKey) {
      return true
    }

    // Para daterange: verificar que ambas fechas estén seleccionadas y no haya errores
    if (selectedOption?.type === 'daterange') {
      return !dateRangeStart || !dateRangeEnd || !!dateRangeError
    }

    // Para textempty: siempre habilitado si hay filtro seleccionado
    if (selectedOption?.type === 'textempty') {
      return false
    }

    // Para otros tipos: verificar que haya un valor
    return !inputValue || inputValue.length === 0
  }

  const addFilter = () => {
    if (selectedOption?.type === 'daterange') {
      // Validar que ambas fechas estén presentes
      if (!dateRangeStart || !dateRangeEnd) {
        return
      }
      // Validar que no haya error en el rango de fechas
      if (dateRangeError) {
        return
      }
      // Determinar los keys según el tipo de rango
      let initialKey = 'initialDateFilter'
      let finalKey = 'finalDateFilter'
      let initialLabel = 'Fecha Inicial'
      let finalLabel = 'Fecha Final'
      if (selectedOption?.key === 'movementDateRange') {
        initialKey = 'initialDateFilter'
        finalKey = 'finalDateFilter'
        initialLabel = 'Fecha Inicial Movimiento'
        finalLabel = 'Fecha Final Movimiento'
      } else if (selectedOption?.key === 'expirationDateRange') {
        initialKey = 'initialExpirationDateFilter'
        finalKey = 'finalExpirationDateFilter'
        initialLabel = 'Fecha Inicial Expiración'
        finalLabel = 'Fecha Final Expiración'
      }
      // Crear filtro para fecha inicial
      const initialDateFilter: Filter = {
        key: initialKey,
        label: initialLabel,
        value: dateRangeStart,
        displayValue: dateRangeStart
      }
      // Crear filtro para fecha final
      const finalDateFilter: Filter = {
        key: finalKey,
        label: finalLabel,
        value: dateRangeEnd,
        displayValue: dateRangeEnd
      }
      // Verificar que no existan ya estos filtros
      const existsInitial = selectedFilters.some(
        ({ key }) => key === initialKey
      )
      const existsFinal = selectedFilters.some(({ key }) => key === finalKey)
      if (existsInitial || existsFinal) {
        return
      }
      // Agregar los dos filtros
      setSelectedFilters([
        ...selectedFilters,
        initialDateFilter,
        finalDateFilter
      ])
      // resetea los valores en el estado que tenian por defecto
      setDateRangeStart('')
      setDateRangeEnd('')
      setInputValue('')
      // Eliminar la opción de availableOptions solo si NO es repetible
      if (!selectedOption.canBeRepeated) {
        setAvailableOptions(
          availableOptions.filter((option) => option.key !== selectedOption.key)
        )
      }
      setSelectedFilterKey('')
      setDateRangeError('')
      return
    }
    if (
      selectedOption?.type === 'textempty' ||
      (selectedOption && inputValue)
    ) {
      let displayValue = getDisplayValue(selectedOption.key)
      // Para componentes custom con transformadores, usar el transformador correspondiente
      let filterValue = inputValue
      if (
        selectedOption.type === 'custom' &&
        selectedOption.transformForBackend
      ) {
        filterValue = selectedOption.transformForBackend(inputValue)
        // Si no se pudo obtener displayValue de getDisplayValue, usar el transformador de display
        if (!displayValue && selectedOption.transformForDisplay) {
          displayValue = selectedOption.transformForDisplay(inputValue)
        }
      } else if (selectedOption.type === 'custom') {
        // Fallback: intentar extraer el id y displayValue del JSON
        try {
          const parsed = JSON.parse(inputValue)
          filterValue = parsed.id ? parsed.id.toString() : inputValue
          displayValue = parsed.displayValue || inputValue
        } catch {
          // Si no se puede parsear, usar el valor original
          filterValue = inputValue
          displayValue = inputValue
        }
      }
      const exists = selectedFilters.filter(
        ({ key, value }) => selectedOption.key === key && value === filterValue
      )
      if (exists.length > 0) {
        return
      }
      const newFilter: Filter = {
        key: selectedOption.key,
        label: selectedOption.label,
        value: filterValue,
        displayValue: displayValue || inputValue
      }
      setSelectedFilters([...selectedFilters, newFilter])
      setInputValue('') // Limpiar el valor de entrada después de agregar el filtro
      // Eliminar la opción seleccionada de availableOptions solo si NO es repetible
      if (!selectedOption.canBeRepeated) {
        setAvailableOptions(
          availableOptions.filter((option) => option.key !== selectedOption.key)
        )
      }
      setSelectedFilterKey('')
    }
  }

  const handleDeleteFilter = (filterToDelete: Filter) => {
    // obtener opción
    const deletedOption = options.find(
      (option) => option.key === filterToDelete.key
    )
    // detectar si es repetible
    if (deletedOption?.canBeRepeated) {
      // Para repetibles: solo eliminar el filtro específico (por key y value)
      setSelectedFilters(
        selectedFilters.filter(
          (filter) =>
            !(
              filter.key === filterToDelete.key &&
              filter.value === filterToDelete.value
            )
        )
      )
      // NO agregar de vuelta a availableOptions porque los repetibles siempre deben estar ahí
    } else {
      // Para no repetibles: eliminar todos los filtros con ese key
      const updatedFilters = selectedFilters.filter(
        (filter) => filter.key !== filterToDelete.key
      )
      setSelectedFilters(updatedFilters)
      // Verifica si se eliminaron ambos filtros de fecha para devolver la opcion dateRange
      if (
        filterToDelete.key === 'initialDateFilter' ||
        filterToDelete.key === 'finalDateFilter'
      ) {
        // Verificar si después de eliminar este filtro, ya no quedan filtros de fecha
        const remainingDateFilters = updatedFilters.filter(
          (filter) =>
            filter.key === 'initialDateFilter' ||
            filter.key === 'finalDateFilter'
        )
        // Solo devolver la opcion dateRange si NO quedan filtros de fecha
        if (remainingDateFilters.length === 0) {
          const dateRangeOption = options.find(
            (option) => option.key === 'dateRange'
          )
          if (
            dateRangeOption &&
            !availableOptions.some((opt) => opt.key === 'dateRange')
          ) {
            setAvailableOptions([...availableOptions, dateRangeOption])
          }
        }
      } else {
        // Para filtros normales, agregar la opción eliminada nuevamente a availableOptions
        if (
          deletedOption &&
          !availableOptions.some((opt) => opt.key === deletedOption.key)
        ) {
          setAvailableOptions([...availableOptions, deletedOption])
        }
      }
    }
  }

  const handleEditFilter = (filterToEdit: Filter) => {
    // Hay filtros que se manejan por fuera de este componente
    // Si no existe e intentan editarlo, lo borramos
    if (!options.map((option) => option.key).includes(filterToEdit.key)) {
      setSelectedFilters((prev) =>
        prev.filter((el) => el.key !== filterToEdit.key)
      )
      return
    }
    // Agregar la opción eliminada nuevamente a availableOptions solo si NO es repetible
    const deletedOption = options.find(
      (option) => option.key === filterToEdit.key
    )

    // seleccionar esa opción en el filtro
    setSelectedFilterKey(filterToEdit.key)
    setInputValue(filterToEdit.value)

    // detectar si es repetible
    if (deletedOption?.canBeRepeated) {
      // Para repetibles: solo eliminar el filtro específico (por key y value)
      setSelectedFilters(
        selectedFilters.filter(
          (filter) =>
            !(
              filter.key === filterToEdit.key &&
              filter.value === filterToEdit.value
            )
        )
      )
      // NO agregar a availableOptions porque los repetibles siempre deben estar ahí
    } else {
      // Para no repetibles: eliminar el filtro y agregarlo a availableOptions
      setSelectedFilters(
        selectedFilters.filter((filter) => filter.key !== filterToEdit.key)
      )
      // Agregar la opción eliminada nuevamente a availableOptions
      if (
        deletedOption &&
        !availableOptions.some((opt) => opt.key === deletedOption.key)
      ) {
        setAvailableOptions([...availableOptions, deletedOption])
      }
    }
  }

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      handleSearchButton()
    }
  }

  const isButtonDisabled = (): boolean => {
    // Validación para inputValue = '-1' cuando no hay botón agregar
    if (inputValue === '-1' && !includeAddButton) {
      return true
    }

    // Si se especifican filtros requeridos, validar que al menos uno esté presente
    if (requiredFilterKeys && requiredFilterKeys.length > 0) {
      const hasRequiredFilter = selectedFilters.some((filter) =>
        requiredFilterKeys.includes(filter.key)
      )

      if (!hasRequiredFilter) {
        return true
      }
    }

    // Si muestran el botón "agregar filtros"
    if (includeAddButton) {
      // El botón buscar se habilita mientras existan filtros incluidos.
      // Si existen filtros agregados y luego selecciona un nuevo filtro,
      // se bloquea buscar hasta que agregue el filtro.

      // Para daterange: permitir buscar solo si no hay filtro pendiente de agregar
      if (selectedOption?.type === 'daterange') {
        return (
          selectedFilters.length === 0 ||
          !!(dateRangeStart && dateRangeEnd && !dateRangeError)
        )
      }

      // Para otros tipos: comportamiento original
      return selectedFilters.length === 0 || inputValue.length !== 0
    }

    // Comportamiento por defecto (sin botón agregar)
    // Validación específica para daterange
    if (selectedOption?.type === 'daterange') {
      return !dateRangeStart || !dateRangeEnd || !!dateRangeError
    }

    return selectedOption?.type === 'textempty'
      ? false
      : !selectedOption || inputValue.length === 0
  }
  const setFormatTextChip = (filter: Filter) => {
    switch (filter.key) {
      case 'initialDeliveryDate':
      case 'finalDeliveryDate':
      case 'initialDateFilter':
      case 'finalDateFilter':
        return `${filter.label}: ${DateTime.fromISO(
          filter.displayValue
        ).toFormat('dd-MM-yyyy')}`
      default:
        return `${filter.label}: ${filter.displayValue}`
    }
  }

  // Se agrega metodo para obtener id de warehouse en caso que esté agregado en los filtros. si sacan los filtros queda null y muestra todas las zonas el DDL de Zona
  const getWarehouseId = () => {
    const id = selectedFilters.find((el) => el.key === 'warehouseId')?.value
    if (id && id > 0) return parseInt(id, 10)

    return null
  }

  const renderFilterInput = (
    option: FilterOption,
    inputValue: string,
    handleInputChange: any,
    selectedFilters: Filter[]
  ) => {
    switch (option.type) {
      case 'numeric':
        return (
          <TextField
            fullWidth
            type='number'
            value={inputValue}
            onChange={handleInputChange}
            variant='outlined'
          />
        )
      case 'numericnotnegative':
        return (
          <TextField
            fullWidth
            type='number'
            value={inputValue}
            onChange={handleInputChangeNumericNotNegative}
            variant='outlined'
            inputProps={{ min: 0 }}
          />
        )
      case 'text':
        return (
          <TextField
            fullWidth
            value={inputValue}
            onChange={handleInputChange}
            variant='outlined'
            autoFocus={true}
            onKeyPress={handleKeyPress}
          />
        )
      case 'textempty':
        return (
          <TextField
            fullWidth
            value={inputValue}
            onChange={handleInputChange}
            variant='outlined'
          />
        )
      case 'date':
        return (
          <TextField
            type='date'
            value={inputValue}
            onChange={handleInputChange}
            InputLabelProps={{ shrink: true }}
            fullWidth
            variant='outlined'
          />
        )
      case 'range':
        return <TextField fullWidth /* props */ />
      case 'precreated':
        if (option.key === 'positionCode') {
          return (
            <PositionAutocomplete
              handleChange={handleInputChange}
              positionValue={inputValue}
              fullWidth
              variant='outlined'
              size='medium'
            />
          )
        }
        if (option.key === 'externalStatusId') {
          const externalStatusOptions = Object.keys(eStatus)
            .filter((key) => isNaN(Number(key))) // Filtra las claves numéricas
            .map((key) => ({
              value: eStatus[key as keyof typeof eStatus],
              label: key
            }))

          return (
            <TextField
              variant={'outlined'}
              id='statusId'
              name='statusId'
              select
              value={inputValue}
              onChange={handleInputChange}
              fullWidth
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar Estado...'}
              </MenuItem>
              {externalStatusOptions.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </TextField>
          )
        }
        if (option.key === 'typeActionId') {
          const externalStatusOptions = Object.keys(eTypeAction)
            .filter((key) => isNaN(Number(key))) // Filtra las claves numéricas
            .map((key) => ({
              value: eTypeAction[key as keyof typeof eTypeAction],
              label: key
            }))

          return (
            <TextField
              variant={'outlined'}
              id='typeActionId'
              name='typeActionId'
              select
              value={inputValue}
              onChange={handleInputChange}
              fullWidth
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar Tipo de Acción...'}
              </MenuItem>
              {externalStatusOptions.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </TextField>
          )
        }
        if (option.key === 'StatusProcessId') {
          const externalStatusOptions = Object.keys(eStatusProcess)
            .filter((key) => isNaN(Number(key))) // Filtra las claves numéricas
            .map((key) => ({
              value: eStatusProcess[key as keyof typeof eStatusProcess],
              label: key
            }))

          return (
            <TextField
              variant={'outlined'}
              id='StatusProcessId'
              name='StatusProcessId'
              select
              value={inputValue}
              onChange={handleInputChange}
              fullWidth
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar estado proceso...'}
              </MenuItem>
              {externalStatusOptions.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </TextField>
          )
        }
        if (option.key === 'productOriginId' || option.key === 'brandId') {
          return (
            <TextField
              variant={'outlined'}
              id='productOriginId'
              name='productOriginId'
              select
              value={inputValue}
              onChange={handleInputChange}
              fullWidth
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar Marca...'}
              </MenuItem>
              {productOrigins?.map((option: ProductOrigin) => (
                <MenuItem
                  key={option.productOriginId}
                  value={option.productOriginId}
                >
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          )
        }
        if (option.key === 'typePositionId') {
          return (
            <TypePositionDDL
              onChange={handleInputChange}
              value={inputValue}
              fullWidth
              variant='outlined'
            />
          )
        }
        if (option.key === 'typeMovementId') {
          return (
            <TypeMovementDDL
              onChange={handleInputChange}
              value={inputValue}
              variant='outlined'
              fullWidth={true}
            />
          )
        }
        if (option.key === 'warehouseId') {
          return (
            <WarehouseDDL
              fullWidth
              onChange={handleInputChange}
              value={inputValue}
              type='outlined'
            />
          )
        }
        if (option.key === 'zoneId') {
          return (
            <Fragment>
              <ZoneDDL
                warehouseId={getWarehouseId()}
                fullWidth
                onChange={handleInputChange}
                value={inputValue}
                variant='outlined'
              />
            </Fragment>
          )
        }
        if (option.key === 'typeOrderId') {
          return (
            <TextField
              id='typeOrderId'
              name='typeOrderId'
              select
              value={inputValue || '-1'}
              onChange={handleInputChange}
              variant='outlined'
              fullWidth
            >
              <MenuItem key={'-1'} value={'-1'}>
                {'Seleccionar Tipo de Pedido...'}
              </MenuItem>
              {typesOrder?.map((typeOrder) => (
                <MenuItem
                  key={typeOrder.typeOrderId}
                  value={typeOrder.typeOrderId}
                >
                  {typeOrder.name} ({typeOrder.description})
                </MenuItem>
              ))}
            </TextField>
          )
        }
        if (option.key === 'workOrderStateId') {
          return (
            <WorkOrderStateDDL
              onChange={handleInputChange}
              value={inputValue}
              fullWidth
              variant='outlined'
            />
          )
        }
        if (option.key === 'orderOriginId') {
          return (
            <Fragment>
              <GenericDLL
                fullWidth
                initialValues={orderOrigins || []}
                onChange={handleInputChange}
                title='Origen Marca'
                name='origin'
                valueKey={'orderOriginId'}
                value={inputValue}
                variant='outlined'
                excludedOptions={
                  selectedFilters.find((el) => el.key === 'orderOriginId')
                    ?.value
                }
              />
            </Fragment>
          )
        }
        if (option.key === 'flowId') {
          return (
            <Fragment>
              <GenericDLL
                fullWidth
                initialValues={flows || []}
                onChange={handleInputChange}
                title='Origen'
                name='description'
                valueKey={'flowId'}
                value={inputValue}
                variant='outlined'
                excludedOptions={
                  selectedFilters.find((el) => el.key === 'flowId')?.value
                }
              />
            </Fragment>
          )
        }
        if (option.key === 'typeAlertId') {
          return (
            <Fragment>
              <GenericDLL
                fullWidth
                initialValues={typesAlert || []}
                onChange={handleInputChange}
                title='Tipos de Alerta'
                name='name'
                valueKey={'typeAlertId'}
                value={inputValue}
                variant='outlined'
                excludedOptions={
                  selectedFilters.find((el) => el.key === 'typeAlertId')?.value
                }
              />
            </Fragment>
          )
        }
        if (option.key === 'typeProcessId') {
          return (
            <Fragment>
              <GenericDLL
                fullWidth
                initialValues={typeProcess || []}
                onChange={handleInputChange}
                title='Tipo Proceso'
                name='description'
                valueKey={'typeProcessId'}
                value={inputValue}
                variant='outlined'
                excludedOptions={
                  selectedFilters.find((el) => el.key === 'typeProcessId')
                    ?.value
                }
              />
            </Fragment>
          )
        }
        if (option.key === 'orderStateId') {
          return (
            <TextField
              id='orderStateId'
              name='orderStateId'
              select
              value={inputValue || '-1'}
              onChange={handleInputChange}
              variant='outlined'
              fullWidth
            >
              <MenuItem key={'-1'} value={'-1'}>
                {'Seleccionar Estado...'}
              </MenuItem>
              {option.options?.map((stateOption) => (
                <MenuItem key={stateOption.value} value={stateOption.value}>
                  {stateOption.label}
                </MenuItem>
              ))}
            </TextField>
          )
        }
        if (option.key === 'asnStateId') {
          const filteredAsnState = option.keyfilter
            ? asnStates?.filter((state) =>
                option.keyfilter!.includes(state.asnStateId.toString())
              )
            : asnStates || []

          // Obtener valores ya seleccionados y normalizarlos a número
          const excludedValues = selectedFilters
            .filter((filter) => filter.key === 'asnStateId')
            .map((filter) => {
              // Convertir a número para comparación consistente
              return typeof filter.value === 'string'
                ? parseInt(filter.value, 10)
                : filter.value
            })

          // Filtrar manualmente los estados que ya están seleccionados
          const availableStates = (filteredAsnState || []).filter(
            (state) => !excludedValues.includes(state.asnStateId) // Comparar número con número
          )

          return (
            <Fragment>
              <GenericDLL
                fullWidth
                initialValues={availableStates} // Pasar solo los estados disponibles
                onChange={handleInputChange}
                title='Estado Asn'
                name='name'
                valueKey={'asnStateId'}
                value={inputValue}
                variant='outlined'
              />
            </Fragment>
          )
        }
        if (option.key === 'typeAsnIncomeId') {
          const filteredTypeAsnIncomeState = option.keyfilter
            ? typeAsnIncome?.filter((state) =>
                option.keyfilter!.includes(state.typeAsnIncomeId.toString())
              )
            : typeAsnIncome || []

          return (
            <Fragment>
              <GenericDLL
                fullWidth
                initialValues={filteredTypeAsnIncomeState || []}
                onChange={handleInputChange}
                title='Estado Asn'
                name='name'
                valueKey={'typeAsnIncomeId'}
                value={inputValue}
                variant='outlined'
                excludedOptions={
                  selectedFilters.find((el) => el.key === 'typeAsnIncomeId')
                    ?.value
                }
              />
            </Fragment>
          )
        }
        if (option.key === 'inventoryCheckStateId') {
          const inventoryCheckOptions = Object.keys(eInventoryCheckState)
            .filter((key) => isNaN(Number(key))) // Filtra las claves numéricas
            .map((key) => ({
              value:
                eInventoryCheckState[key as keyof typeof eInventoryCheckState],
              label: key
            }))

          return (
            <TextField
              variant={'outlined'}
              id='inventoryCheckstateId'
              name='inventoryCheckstateId'
              select
              value={inputValue}
              onChange={handleInputChange}
              fullWidth
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar Estado...'}
              </MenuItem>
              {inventoryCheckOptions.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </TextField>
          )
        }
        if (option.key === 'batchExpiredStatus') {
          const inventoryCheckOptions = Object.keys(eBatchExpiredStatus)
            .filter((key) => isNaN(Number(key))) // Filtra las claves numéricas
            .map((key) => ({
              value:
                eBatchExpiredStatus[key as keyof typeof eBatchExpiredStatus],
              label: key
            }))

          return (
            <TextField
              variant={'outlined'}
              id='batchExpiredStatus'
              name='batchExpiredStatus'
              select
              value={inputValue}
              onChange={handleInputChange}
              fullWidth
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar...'}
              </MenuItem>
              {inventoryCheckOptions.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </TextField>
          )
        }
        if (option.key === 'batchControl') {
          const batchControlOptions = Object.keys(eBatchControl)
            .filter((key) => isNaN(Number(key))) // Filtra las claves numéricas
            .map((key) => ({
              value: eBatchControl[key as keyof typeof eBatchControl],
              label: key
            }))

          return (
            <TextField
              variant={'outlined'}
              id='batchControl'
              name='batchControl'
              select
              value={inputValue}
              onChange={handleInputChange}
              fullWidth
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar...'}
              </MenuItem>
              {batchControlOptions.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </TextField>
          )
        }
        if (option.key === 'isGreenSale') {
          const isGreenSaleOptions = Object.keys(eIsGreenSale)
            .filter((key) => isNaN(Number(key))) // Filtra las claves numéricas
            .map((key) => ({
              value: eIsGreenSale[key as keyof typeof eIsGreenSale],
              label: key
            }))

          return (
            <TextField
              variant={'outlined'}
              id='isGreenSale'
              name='isGreenSale'
              select
              value={inputValue}
              onChange={handleInputChange}
              fullWidth
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar...'}
              </MenuItem>
              {isGreenSaleOptions.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </TextField>
          )
        }
        if (option.key === 'typeInventoryCheckId') {
          const inventoryCheckOptions = Object.keys(eTypeInventoryCheck)
            .filter((key) => isNaN(Number(key))) // Filtra las claves numéricas
            .map((key) => ({
              value:
                eTypeInventoryCheck[key as keyof typeof eTypeInventoryCheck],
              label: key
            }))

          return (
            <TextField
              variant={'outlined'}
              id='typeInventoryCheckId'
              name='typeInventoryCheckId'
              select
              value={inputValue}
              onChange={handleInputChange}
              fullWidth
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar Tipo...'}
              </MenuItem>
              {inventoryCheckOptions.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </TextField>
          )
        }
        if (option.key === 'courierId') {
          const arrCourierDDLItems =
            couriers !== undefined
              ? couriers.map((option: Courier) => (
                  <MenuItem key={option.courierId} value={option.courierId}>
                    {option.name}
                  </MenuItem>
                ))
              : []

          return (
            <TextField
              id='courierId'
              name='courierId'
              select
              value={inputValue || '-1'}
              onChange={handleInputChange}
              variant='outlined'
              fullWidth
            >
              <MenuItem key={'-1'} value={'-1'}>
                {'Seleccionar Courier...'}
              </MenuItem>
              {arrCourierDDLItems}
            </TextField>
          )
        }
        if (option.key === 'courierServiceId') {
          // 1. Creamos una lista plana de TODOS los servicios
          //    y guardamos el nombre del courier padre para claridad.
          const allServicesWithName = couriers
            ? couriers.flatMap((courier) =>
                courier.courierServices.map((service) => ({
                  ...service,
                  courierName: courier.name // Añadimos el nombre del courier
                }))
              )
            : []

          // 2. Verificamos si hay servicios
          if (allServicesWithName.length < 1) {
            return (
              <Typography variant='body2'>
                No hay servicios de courier disponibles.
              </Typography>
            )
          }

          // 3. Creamos los <MenuItem> a partir de la lista plana
          const arrCourierServicesDDLItems = allServicesWithName.map(
            (option) => (
              <MenuItem
                key={`${option.courierName}-${option.courierServiceId}`} // Clave más robusta
                value={option.courierServiceId}
              >
                {/* Mostramos ambos nombres para evitar confusión */}
                {option.courierName} - {option.name}
              </MenuItem>
            )
          )

          // 4. Renderizamos el dropdown
          return (
            <TextField
              id='courierServiceId'
              name='courierServiceId'
              select
              value={inputValue || -1}
              onChange={handleInputChange}
              variant='outlined'
              fullWidth
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar Servicio de Courier...'}
              </MenuItem>
              {arrCourierServicesDDLItems}
            </TextField>
          )
        }
        if (option.key === 'active') {
          return (
            <TextField
              variant='outlined'
              id='active'
              name='active'
              select
              value={inputValue || '-1'}
              onChange={handleInputChange}
              fullWidth
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar Estado...'}
              </MenuItem>
              <MenuItem key={0} value={'false'}>
                No
              </MenuItem>
              <MenuItem key={1} value={'true'}>
                Sí
              </MenuItem>
            </TextField>
          )
        }
        if (option.key === 'isDraft') {
          return (
            <TextField
              variant='outlined'
              id='isDraft'
              name='isDraft'
              select
              value={inputValue || '-1'}
              onChange={handleInputChange}
              fullWidth
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar Borrador...'}
              </MenuItem>
              <MenuItem key={0} value={'false'}>
                No
              </MenuItem>
              <MenuItem key={1} value={'true'}>
                Sí
              </MenuItem>
            </TextField>
          )
        }
        if (option.key === 'asnStateId' || option.key === 'detailAsnStateId') {
          return (
            <TextField
              variant='outlined'
              id={option.key}
              name={option.key}
              select
              value={inputValue || '-1'}
              onChange={handleInputChange}
              fullWidth
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar Estado...'}
              </MenuItem>
              {asnStates?.map((state: AsnState) => (
                <MenuItem key={state.asnStateId} value={state.asnStateId}>
                  {state.name}
                </MenuItem>
              ))}
            </TextField>
          )
        }
        break
      case 'custom':
        if (option.Component) {
          return (
            <option.Component
              value={inputValue}
              onChange={handleInputChange}
              fullWidth={true}
              variant='outlined'
              transformForBackend={option.transformForBackend}
              transformForDisplay={option.transformForDisplay}
            />
          )
        }
        break
      case 'daterange':
        return (
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField
                type='date'
                label='Fecha Inicial'
                value={dateRangeStart}
                onChange={handleDateRangeStartChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                variant='outlined'
                size='small'
                error={!!dateRangeError}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                type='date'
                label='Fecha Final'
                value={dateRangeEnd}
                onChange={handleDateRangeEndChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                variant='outlined'
                size='small'
                error={!!dateRangeError}
              />
            </Grid>
            {dateRangeError && (
              <Grid item xs={12}>
                <Typography
                  variant='caption'
                  color='error'
                  style={{ fontSize: '0.75rem' }}
                >
                  {dateRangeError}
                </Typography>
              </Grid>
            )}
          </Grid>
        )
      default:
        return <Typography>No se ha encontrado el filtro</Typography>
    }

    return null
  }

  const groupedFilters = selectedFilters.reduce((acc, item) => {
    acc[item.key] = acc[item.key] || []
    acc[item.key].push(item)
    return acc
  }, {} as Record<string, Filter[]>)

  const uniqueKeys: Filter[] = []
  const repeatedKeys: {
    key: string
    items: Filter[]
  }[] = []

  const capitalize = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()

  Object.entries(groupedFilters).forEach(([key, items]) => {
    if (items.length > 1) {
      repeatedKeys.push({ key, items })
    } else {
      uniqueKeys.push(...items)
    }
  })

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={includeAddButton ? 3 : 4}>
        <FormControl fullWidth>
          <Select
            value={selectedFilterKey || ''}
            onChange={handleFilterSelection}
            displayEmpty
            variant='outlined'
          >
            <MenuItem value=''>
              <em>
                {availableOptions.length > 0
                  ? `Seleccionar...`
                  : 'Ha seleccionado todos los filtros disponibles.'}
              </em>
            </MenuItem>
            {availableOptions
              .sort((a, b) => a.order - b.order)
              .map((option) => (
                <MenuItem key={option.key} value={option.key}>
                  {option.label}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid
        container
        item
        xs={12}
        md={includeRefreshSearch ? 3 : 4}
        alignContent='center'
        alignItems='center'
        justifyContent='center'
      >
        {selectedOption &&
          availableOptions.length > 0 &&
          renderFilterInput(
            selectedOption,
            inputValue,
            handleInputChange,
            selectedFilters
          )}
        {!selectedOption && <Typography>Debe seleccionar un filtro</Typography>}
      </Grid>
      {includeAddButton && (
        <Tooltip title='Agregar filtro de busqueda'>
          <Grid
            item
            xs={12}
            md={2}
            container
            alignContent='center'
            alignItems='center'
            justifyContent='center'
          >
            <Button
              onClick={() => addFilter()}
              variant='contained'
              color='primary'
              fullWidth
              disabled={isAddFilterButtonDisabled()}
            >
              Agregar filtro
            </Button>
          </Grid>
        </Tooltip>
      )}
      <Grid
        container
        item
        xs={12}
        md={2}
        alignContent='center'
        alignItems='center'
        justifyContent='center'
      >
        <Button
          onClick={() => handleSearchButton()}
          variant='contained'
          color='primary'
          fullWidth
          disabled={isButtonDisabled()}
        >
          Buscar
        </Button>
      </Grid>
      <Grid
        container
        item
        xs={12}
        md={includeAddButton ? 1 : 2}
        alignContent='center'
        alignItems='center'
        justifyContent='center'
      >
        <Button
          onClick={() => {
            setSelectedFilters([])
            setAvailableOptions(options)
            setSelectedFilterKey('')
            setInputValue('')
            setDateRangeStart('')
            setDateRangeEnd('')
            setDateRangeError('')
          }}
          variant='outlined'
          color='primary'
          fullWidth
        >
          Limpiar
        </Button>
      </Grid>
      {includeRefreshSearch && (
        <Grid
          container
          item
          xs={12}
          md={1}
          alignContent='center'
          alignItems='center'
          justifyContent='center'
        >
          <IconButton
            color='primary'
            onClick={() => {
              onSearchClick(selectedFilters)
            }}
            disabled={selectedFilters.length === 0}
          >
            <RotateLeftIcon />
          </IconButton>
        </Grid>
      )}

      <Grid container item xs={12}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px'
          }}
        >
          {uniqueKeys.map((item) => (
            <Chip
              key={item.key}
              label={
                item.label === ''
                  ? item.displayValue
                  : `${item.label}: ${capitalize(item.displayValue)}`
              }
              // style={{ backgroundColor: 'lightblue' }}
              onClick={() => handleEditFilter(item)}
              onDelete={() => handleDeleteFilter(item)}
            />
          ))}
        </div>
      </Grid>

      <Grid container item xs={12}>
        {repeatedKeys.map(({ key, items }) => (
          <Fragment>
            {items.length > 1 ? (
              <div
                key={key}
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '10px',
                  padding: '10px',
                  border: '1px dashed #ccc',
                  borderRadius: '10px'
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: '-1px', // Ajustar según el borde
                    left: '10px',
                    backgroundColor: '#fff',
                    padding: '0 5px',
                    color: '#333',
                    fontWeight: 'bold',
                    transform: 'translateY(-50%)'
                  }}
                >
                  <Typography style={{ marginRight: '10px' }}>
                    {items[0].label}
                  </Typography>
                </div>

                {items.map((item) => (
                  <Chip
                    key={item.value}
                    label={capitalize(item.displayValue)}
                    onClick={() => handleEditFilter(item)}
                    onDelete={() => handleDeleteFilter(item)}
                  />
                ))}
              </div>
            ) : (
              <Chip
                key={key}
                label={setFormatTextChip(items[0])}
                onClick={() => handleEditFilter(items[0])}
                onDelete={() => handleDeleteFilter(items[0])}
              />
            )}
          </Fragment>
        ))}
      </Grid>
    </Grid>
  )
}
